import { authFetch, getAuthHeaders } from '../lib/authFetch';

const getBaseUrl = (url: string) => url.startsWith('http') ? url : `https://${url}`;

const API_URL = import.meta.env.VITE_API_BASE_URL
    ? `${getBaseUrl(import.meta.env.VITE_API_BASE_URL)}/api/users`
    : 'https://stingray-app-2pifp.ondigitalocean.app/api/users';

export const loginUser = async (emailOrPhone: string, password: string, isEmail: boolean): Promise<any> => {
    const response = await fetch(`${API_URL}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            [isEmail ? 'email' : 'phone']: emailOrPhone,
            password,
        }),
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Login failed');
    }

    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
        throw new Error(`Invalid Response! App tried to reach ${API_URL}/login but got HTML. Error Code: ${response.status}`);
    }

    const data = await response.json();
    localStorage.setItem('userInfo', JSON.stringify(data));
    return data;
};

export const registerUser = async (name: string, email: string, phone: string, password: string, idNumber?: string, invitationCode?: string, countryIso?: string): Promise<any> => {
    const response = await fetch(`${API_URL}/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, phone, password, idNumber, invitationCode, countryIso }),
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Registration failed');
    }

    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
        throw new Error(`Invalid Response! App tried to reach ${API_URL}/register but got HTML. Error Code: ${response.status}`);
    }

    const data = await response.json();
    localStorage.setItem('userInfo', JSON.stringify(data));
    return data;
};

export const verifyOTP = async (email: string, otp: string): Promise<any> => {
    const response = await fetch(`${API_URL}/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp }),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'OTP verification failed');
    }
    return response.json();
};

export const resendOTP = async (email: string): Promise<any> => {
    const response = await fetch(`${API_URL}/resend-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to resend OTP');
    }
    return response.json();
};

export const forgotPassword = async (email: string): Promise<any> => {
    const response = await fetch(`${API_URL}/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to send reset code');
    }
    return response.json();
};

export const verifyResetOTP = async (email: string, otp: string): Promise<any> => {
    const response = await fetch(`${API_URL}/verify-reset-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp }),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Invalid reset code');
    }
    return response.json();
};

export const resetPassword = async (email: string, resetToken: string, newPassword: string): Promise<any> => {
    const response = await fetch(`${API_URL}/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, resetToken, newPassword }),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Password reset failed');
    }
    return response.json();
};

export const getProfile = async (): Promise<any> => {
    const response = await authFetch(`${API_URL}/profile`, {
        method: 'GET',
        headers: getAuthHeaders(),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to get profile');
    }
    return response.json();
};

export const submitKYC = async (documentType: string, documentFront: string, documentBack: string, selfie: string, country?: string, fullName?: string, idNumber?: string): Promise<any> => {
    const response = await authFetch(`${API_URL}/kyc`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ documentType, documentFront, documentBack, selfie, country, fullName, idNumber }),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'KYC submission failed');
    }
    return response.json();
};

export const checkKycIdAvailable = async (idNumber: string): Promise<{ available: boolean; message?: string }> => {
    const response = await authFetch(`${API_URL}/kyc/check-id`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ idNumber }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
        return { available: false, message: data.message || 'This ID/passport/license number is already registered with another account.' };
    }
    return data;
};

export const getKYCStatus = async (): Promise<any> => {
    const response = await authFetch(`${API_URL}/kyc-status`, {
        method: 'GET',
        headers: getAuthHeaders(),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to get KYC status');
    }
    return response.json();
};

export const logoutUser = () => {
    localStorage.removeItem('userInfo');
};

export const getNotifications = async (): Promise<any[]> => {
    const response = await authFetch(`${API_URL}/notifications`, {
        headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch notifications');
    return response.json();
};

export const markNotificationRead = async (id: string): Promise<any> => {
    const response = await authFetch(`${API_URL}/notifications/${id}/read`, {
        method: 'PUT',
        headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to mark notification as read');
    return response.json();
};

export const getUnreadNotificationCount = async (): Promise<number> => {
    const response = await authFetch(`${API_URL}/notifications/unread-count`, {
        headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to get unread count');
    const data = await response.json();
    return data.count;
};

export const getReferralStats = async (): Promise<any[]> => {
    const response = await authFetch(`${API_URL}/referrals`, {
        method: 'GET',
        headers: getAuthHeaders(),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to get referral stats');
    }
    return response.json();
};

export const updateUserProfile = async (profileData: { name?: string, phone?: string, country?: string, countryIso?: string, notificationPreferences?: any }): Promise<any> => {
    const response = await authFetch(`${API_URL}/profile`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(profileData),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to update profile');
    }
    const data = await response.json();
    const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
    localStorage.setItem('userInfo', JSON.stringify({ ...userInfo, ...data, isProfileComplete: true }));
    return data;
};

export const changePassword = async (currentPassword: string, newPassword: string): Promise<any> => {
    const response = await authFetch(`${API_URL}/change-password`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ currentPassword, newPassword }),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to change password');
    }
    return response.json();
};

export const changeEmail = async (newEmail: string, password: string): Promise<any> => {
    const response = await authFetch(`${API_URL}/change-email`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ newEmail, password }),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to change email');
    }
    const data = await response.json();
    const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
    localStorage.setItem('userInfo', JSON.stringify({ ...userInfo, email: data.email }));
    return data;
};

export const deleteAccountService = async (password: string): Promise<any> => {
    const response = await authFetch(`${API_URL}/account`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        body: JSON.stringify({ password }),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to delete account');
    }
    localStorage.removeItem('userInfo');
    return response.json();
};

export const setFundPasswordService = async (currentPassword: string, newFundPassword: string): Promise<any> => {
    const response = await authFetch(`${API_URL}/fund-password`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ currentPassword, newFundPassword }),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to set fund password');
    }
    return response.json();
};

// Theme — localStorage only, no API call
export const updateTheme = (_theme: 'dark' | 'light'): void => {
    localStorage.setItem('bicoin_theme', _theme);
};

export const updateNotificationPreferences = async (prefs: { trade?: boolean; system?: boolean; market?: boolean }): Promise<void> => {
    const response = await authFetch(`${API_URL}/notification-preferences`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(prefs),
    });
    if (!response.ok) throw new Error('Failed to update notification preferences');
};

export const getDeviceSessionsService = async (): Promise<any[]> => {
    const response = await fetch(`${API_URL}/devices`, { headers: getAuthHeaders() });
    if (!response.ok) return [];
    return response.json();
};

export const revokeDeviceSessionService = async (sessionId: string): Promise<void> => {
    await fetch(`${API_URL}/devices/revoke`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId }),
    });
};

export const getAnnouncementsService = async (): Promise<{ title: string; body: string }[]> => {
    try {
        const response = await fetch(`${API_URL}/announcements`);
        const data = await response.json();
        return Array.isArray(data.data) ? data.data : [];
    } catch { return []; }
};

export const getBannersService = async (): Promise<{ _id: string; imageUrl: string; linkUrl?: string; title?: string }[]> => {
    try {
        const response = await fetch(`${API_URL}/banners`);
        const data = await response.json();
        return Array.isArray(data.data) ? data.data : [];
    } catch { return []; }
};

export const verify2FALogin = async (preAuthToken: string, code: string): Promise<any> => {
    const response = await fetch(`${API_URL}/2fa/verify-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ preAuthToken, code }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'Invalid code');
    localStorage.setItem('userInfo', JSON.stringify(data));
    return data;
};

export const get2FASetup = async (): Promise<{ secret: string; otpauthUrl: string }> => {
    const response = await fetch(`${API_URL}/2fa/setup`, { headers: getAuthHeaders() });
    const data = await response.json();
    if (!data.success) throw new Error(data.message || 'Failed to generate 2FA secret');
    return data.data;
};

export const enable2FAService = async (secret: string, code: string): Promise<void> => {
    const response = await fetch(`${API_URL}/2fa/enable`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ secret, code }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'Failed to enable 2FA');
};

export const disable2FAService = async (code: string): Promise<void> => {
    const response = await fetch(`${API_URL}/2fa/disable`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'Failed to disable 2FA');
};
